import { FooterLink } from '../components/_events/_manage-event/_edit-website/website-form-body/website-footer-links/website-footer-link.presentation';
import { EditWebsiteParams } from '../components/_events/_manage-event/_edit-website/edit-website.viewmodel';
import { JobDtoType } from '../domain/jobs';
import { IEventRequestParams, IImageWithObjectKey, IImageWithUrl, IPaginationParams, ISortRequestParams } from './shared.dto';
import { IUserDetailsDTO } from './users.dao';
import { ICsvColumn } from './connections.dao';
import { IEventLinkDTO } from './event-links.dao';

abstract class IEventsDAO {
    public abstract getEvents(
        params:
            { accountId: number; deliveryTeamMembers?: string; coordinators?: string }
            & IPaginationParams
            & ISortRequestParams<TGetEventsSortKeys>
    ): Promise<IEventsListDTO>;
    public abstract getAllEvents(
        params: IPaginationParams & ISortRequestParams<TGetEventTrackerSortKeys> & IEventTrackerFilterParams
    ): Promise<IEventsTrackerListDTO>;
    public abstract getEventDetails(params: { eventId: number; accountId: number }): Promise<IEventDetailsDTO>;
    public abstract updateEvent(accountId: number, updateEventDTO: Omit<IUpdateEventDTO, 'links'>): Promise<IEventDetailsDTO>;
    public abstract createEvent(accountId: number, createEventDTO: ICreateEventDTO): Promise<IEventDetailsDTO>;
    public abstract updateEventWebsite(params: IEventRequestParams, editWebsiteParams: EditWebsiteParams): Promise<ICreateEventDTO>;
    public abstract deleteEvent(params: IEventRequestParams): Promise<JobDtoType>;
    public abstract getEventEditPassportDetails(params: IEventRequestParams): Promise<IEditPassportDetailsDTO>;
    public abstract updateEventEditPassportDetails(params: IEventRequestParams & {subdomain: string; themeId: number}): Promise<IEditPassportDetailsDTO>;
    public abstract getAllEventConnectionExportTemplate(params: {eventId: number; accountId: number}): Promise<IAllEventConnectionExportDetails>;
    public abstract getEventTrackerFilterViews(params: {
        filterViewTab: EVENT_TRACKER_FILTER_VIEW_TAB;
    }): Promise<IFiltersView[]>;
    public abstract createEventTrackerFilterView(view: IFiltersView): Promise<IFiltersView>;
    public abstract deleteEventTrackerFilterView(params: { userFilterViewId: number }): Promise<void>;
    public abstract createEventRegistrationToken(params: IEventRequestParams): Promise<IEventRegistrationToken>;
    public abstract updateEventGuidlines(params: IEventRequestParams & {
        eventNotes: string;
    }): Promise<Pick<IEventDetailsDTO, 'eventNotes'>>;
    public abstract getAgentMemory(params: IEventRequestParams): Promise<IAgentMemoryDTO>;
    public abstract updateAgentMemory(params: IEventRequestParams & {
        agentMemory: string;
    }): Promise<IAgentMemoryDTO>;
}

export enum EventAccess {
    Open = 'Open',
    Restricted = 'Restricted'
}

export enum EventStatus {
    IN_PROGRESS = 'In Progress',
    DELIVERED = 'Delivered',
    CANCELLED = 'Cancelled'
}

interface IEventLinks {
    value: EventLinksValues;
    label: string;
}

export enum EventLinksValues {
    PASSPORT = 'Passport',
}

interface IEventRegions {
    value: EventRegionsValues;
    label: string;
}

export enum EventRegionsValues {
    AMER = 'AMER',
    APAC = 'APAC',
    EMEA = 'EMEA'
}

interface IEventTypes {
    value: EventTypesValues;
    label: string;
}

export enum EventTypesValues {
    AGM = 'AGM',
    ANALYST_MARKETING = 'ANALYST_MARKETING',
    BUSINESS_MEETINGS = 'BUSINESS_MEETINGS',
    CONFERENCE = 'CONFERENCE',
    DAY_OF_COMMS_LINE = 'DAY_OF_COMMS_LINE',
    DEAL_ROADSHOW = 'DEAL_ROADSHOW',
    EARNINGS_RELEASE = 'EARNINGS_RELEASE',
    EDUCATION_TRAINING = 'EDUCATION_TRAINING',
    EXPERT_EVENT = 'EXPERT_EVENT',
    FIELD_TRIP = 'FIELD_TRIP',
    INTERNAL_COMMUNICATIONS = 'INTERNAL_COMMUNICATIONS',
    INVESTOR_CAPITAL_MARKETS_DAY = 'INVESTOR_CAPITAL_MARKETS_DAY',
    NETWORKING_EVENT = 'NETWORKING_EVENT',
    NON_DEAL_ROADSHOW = 'NON_DEAL_ROADSHOW',
    OE_INTERNAL = 'OE_INTERNAL',
    ONSITE_HYBRID = 'ONSITE_HYBRID',
    TRADE_SHOW = 'TRADE_SHOW',
}

export type TEventTypeLabel =
    | 'AGM'
    | 'Analyst Marketing'
    | 'Business Meetings'
    | 'Conference'
    | 'Day of Comms Line'
    | 'Deal Roadshow'
    | 'Earnings Release'
    | 'Education / Training'
    | 'Expert Event'
    | 'Field Trip'
    | 'Internal Communications'
    | 'Investor / Capital Markets Day'
    | 'Networking Event'
    | 'Non-Deal Roadshow'
    | 'OE Internal'
    | 'Onsite / Hybrid'
    | 'Trade Show'
;

interface IEventBusinessLines {
    value: EventBusinessLinesValues;
    label: string;
}

export enum EventBusinessLinesValues {
    OE_CONNECT = 'OE_CONNECT',
    OE_CONFERENCE = 'OE_CONFERENCE',
    CAPITAL_MARKETS = 'CAPITAL_MARKETS',
    CORPORATE_SERVICES = 'CORPORATE_SERVICES',
    CORPORATE_STREAMLINKS = 'CORPORATE_STREAMLINKS',
    KNOVIO = 'KNOVIO',
    OE_STREAM = 'OE_STREAM',
    PASSPORT = 'PASSPORT',
    OE_INTERNAL_TRAINING = 'OE_INTERNAL_TRAINING',
    OETV = 'OETV',
    BUY_SIDE = 'BUY_SIDE'
}

interface IEventAttributeValue {
    attributeId: number;
    attributeValue?: string;
    multiAttributeValues?: string[];
}

interface ICreateEventDTO {
    name: string;
    privateDescription: string;
    publicDescription: string;
    defaultMeetingDuration?: number;
    startDateTime: Date;
    endDateTime: Date;
    timeZone: string;
    region?: string;
    businessLine?: string;
    type: string;
    eventAccess?: EventAccess;
    oeNumber?: string;
    orderId?: string;
    externalId?: string;
    guidelinesLink?: string;
    projectPlanLink?: string;
    subdomain: string;
    accountSubdomain?: string | null;
    footerLinks: FooterLink[];
    loginErrorMessage?: string;
    loginInstructions?: string;
    preventJoinBefore?: number;
    preventJoinEnabled?: boolean;
    customCss?: string;
    registrationEnabled?: boolean;
    eventNotes?: string;
    coordinators?: IEventCoordinatorDTO[];
    eventCoordinators?: IEventCoordinatorDTO[];
    addedCoordinators?: IEventCoordinatorDTO[];
    removedCoordinators?: IEventCoordinatorDTO[];
    addedDeliveryTeamMembers?: IDeliveryItemDetailsDTO[];
    removedDeliveryTeamMembers?: IDeliveryItemDetailsDTO[];
    updatedDeliveryTeamMembers?: IDeliveryItemDetailsDTO[];
    planningNotes?: string;
    clientPlanningNotes?: string;
    speakersEnabled?: boolean;
    speakersLabel?: string;
    eventStatus?: EventStatus;
    registrationAsLandingPage?: boolean;
    accountId?: number;
    blueprintId?: string | null;
    blueprintInputs?: {
        topic?: string;
    };
    eventTitleForPassport?: string;
    department?: string;
    attributes?: IEventAttributeValue[];
    thumbnailImage?: IImageWithObjectKey;
    thumbnailImageWithUrl?: IImageWithUrl;
}
interface IUpdateEventDTO extends ICreateEventDTO {
    eventId: number;
}

interface IAgentMemoryDTO {
    agentMemory: string | null;
    lastModifiedBy: string | null;
    lastModifiedAt: string | null;
}

interface IEventDetailsDTO extends Omit<ICreateEventDTO,
    | 'addedCoordinators'
    | 'removedCoordinators'
    | 'addedDeliveryTeamMembers'
    | 'removedDeliveryTeamMembers'
    | 'updatedDeliveryTeamMembers'>
{
    eventId: number;
    links: IEventLinkDTO[];
    deliveryTeamMembers: IDeliveryItemDetailsDTO[];
    accountName?: string;
    accountId?: number;
    anonymized: boolean;
    anonymizationDate: Date;
    thirdPartyRegistrationEnabled: boolean;
    eventRegistrationToken: string;
    registrationApprovalEnabled?: boolean;
    requestAccessCodeRegistrationApprovalEnabled?: boolean;
    urlParameterRegistrationEnabled?: boolean;
    passportAttendeeSSOEnabled?: boolean;
    attributes?: IEventAttributeValue[];
    salesforceSynced?: boolean;
    salesforceSyncWarning?: string;
    warningMessage?: string;
}

interface IEventTrackerDetailsDTO {
    accountId: number;
    accountName: string;
    coordinators: IEventCoordinatorDTO[];
    deliveryTeamMembers: IDeliveryItemDetailsDTO[];
    eventId: number;
    name: string;
    oeNumber: string;
    planningNotes: string;
    startDateTime: Date;
    endDateTime: Date;
    mergedDateTime: Date;
    timeZone: string;
    type: string;
    subdomain: string;
    region: string;
    businessLine: string;
    links: IEventLinkDTO[];
    totalPresenters: number;
    totalPresenterCombinationsByDate: number;
    status: EventStatus;
}

interface IAllEventConnectionExportDetails {
    columns: ICsvColumn[];
    name: string;
    connectionExportTemplateId: number;
}

interface IEventCoordinatorDTO {
    username: string;
    firstName: string;
    lastName: string;
    name?: string;
}

type TGetEventsSortKeys = 'name' | 'startDateTime' | 'type';
type TGetEventTrackerSortKeys =
    | 'eventName'
    | 'accountName'
    | 'oeNumber'
    | 'eventType'
    | 'planningNotes'
    | 'eventStartDateTime'
    | 'eventEndDateTime'
    | 'meetingStartDateTime'
    | 'meetingEndDateTime'
    | 'mergedDateTime'
    | 'deliveryTeamMembers';

enum EVENT_TRACKER_TAB {
    UPCOMING = 'UPCOMING',
    PAST = 'PAST',
    DELIVERED = 'DELIVERED'
}

enum EVENT_TRACKER_FILTER_VIEW_TAB {
    EVENT_TRACKER_UPCOMING = 'EVENT_TRACKER_UPCOMING',
    EVENT_TRACKER_PAST = 'EVENT_TRACKER_PAST',
    EVENT_TRACKER_DELIVERED = 'EVENT_TRACKER_DELIVERED'
}

interface IEventTrackerFilterParams {
    deliveryTeamMembers?: string;
    accountIds?: string;
    eventTrackerTab: EVENT_TRACKER_TAB;
    timeZoneOffset: string;
}

interface IEventsListDTO {
    content: IEventDetailsDTO[];
    totalElements: number;
}

interface IEventsTrackerListDTO {
    content: IEventTrackerDetailsDTO[];
    totalElements: number;
}

interface IEditPassportDetailsDTO{
    subdomain: string;
    themeId: number;
}

interface ICoordinatorDetailsDTO extends IUserDetailsDTO{
    name?: string;
}


enum DeliveryTeamMemberRole {
    CO_PROJECT_MANAGER = 'CO_PROJECT_MANAGER',
    EVENT_PRODUCER = 'EVENT_PRODUCER',
    LIVESTREAM_DIRECTOR = 'LIVESTREAM_DIRECTOR',
    MODERATOR = 'MODERATOR',
    OFF_HOURS_LEAD = 'OFF_HOURS_LEAD',
    POLLING_EXPERT = 'POLLING_EXPERT',
    PRODUCTION_ARCHITECT = 'PRODUCTION_ARCHITECT',
    PRODUCTION_LEAD = 'PRODUCTION_LEAD',
    PROJECT_MANAGER = 'PROJECT_MANAGER',
    STUDIO_PRODUCER = 'STUDIO_PRODUCER',
    OTHER = 'OTHER',
}


interface IDeliveryItemDetailsDTO extends IUserDetailsDTO{
    fullName?: string;
    deliveryTeamMemberRole?: string | DeliveryTeamMemberRole;
}

interface IWebsiteDetailsDTO extends IEventDetailsDTO {
    organizationName: string;
    accountId: number;
    eventId: number;
    meetingLinksLabel: string;
    emailConfigurationId?: number;
    emailDisplayName?: string;
}

interface IEntityRoleDTO {
    label: string;
    id: string;
}

interface IFilterValue {
    id: string;
    label: string;
}

interface ISelectedFilter {
    filterName: string;
    filterValues: IFilterValue[];
}

interface IFiltersView {
    userFilterViewId?: number;
    filterViewName: string;
    filters: ISelectedFilter[];
    filterViewTab?: EVENT_TRACKER_FILTER_VIEW_TAB;
}

enum OnSyncEventType {
    ATTENDEE_CREATED = 'ATTENDEE_CREATED',
    ATTENDEE_UPDATED = 'ATTENDEE_UPDATED',
    MEETING_ATTENDEE_UPDATED = 'MEETING_ATTENDEE_UPDATED',
    MEETING_DETAILS_UPDATED = 'MEETING_DETAILS_UPDATED',
    MEETING_OPERATOR_ADDED = 'MEETING_OPERATOR_ADDED',
    MEETING_OPERATOR_REMOVED = 'MEETING_OPERATOR_REMOVED',
    MEETING_OPERATOR_UPDATED = 'MEETING_OPERATOR_UPDATED',
    ROOM_OPERATOR_ADDED = 'ROOM_OPERATOR_ADDED',
    ROOM_OPERATOR_REMOVED = 'ROOM_OPERATOR_REMOVED',
    ROOM_OPERATOR_UPDATED = 'ROOM_OPERATOR_UPDATED',
    LIVE_PRESENTATION_UPDATED = 'LIVE_PRESENTATION_UPDATED',
    LIVE_SESSION_UPDATED = 'LIVE_SESSION_UPDATED',
}
interface IOnSyncEvent {
    id: number;
    eventId: number;
    body: string;
    type: OnSyncEventType;
    dateTime: string;
}

interface IOnSyncEventSubscriptionResponse {
    value: {
        data: {
            onSyncEvent: IOnSyncEvent;
        };
    };
}

interface IEventRegistrationToken {
    eventRegistrationToken: string;
}

enum ClientDepartmentValues {
    ASSET_MANAGEMENT = 'Asset Management',
    CAPITAL_MARKETS = 'Capital Markets',
    CONSUMER_BANKING = 'Consumer Banking',
    CORPORATE_ACCESS = 'Corporate Access',
    CORPORATE_BROKING = 'Corporate Broking',
    CORPORATE_COMMERCIAL_BANKING = 'Corporate/Commercial Banking',
    CORPORATE_COMMS = 'Corporate Comms',
    CORPORATE_SERVICES = 'Corporate Services',
    EQUITIES = 'Equities',
    EVENT_MARKETING = 'Event Marketing',
    FIXED_INCOME = 'Fixed Income',
    FOUNDATION_CHARITABLE_WORK = 'Foundation/Charitable Work',
    GLOBAL_MARKETS = 'Global Markets',
    HUMAN_RESOURCES_RECRUITING = 'Human Resources/Recruiting',
    INVESTOR_RELATIONS = 'Investor Relations',
    INVESTMENT_BANKING = 'Investment Banking',
    ISSUER_SERVICES = 'Issuer Services',
    MARKETING = 'Marketing',
    OTHER = 'Other',
    PRIME_FINANCE_CAPITAL_INTRO = 'Prime Finance/Capital Intro',
    PRIVATE_BANKING_WEALTH_MANAGEMENT = 'Private Banking/Wealth Management',
    RESEARCH = 'Research',
    SECURITY_SERVICES = 'Security Services',
}

export {
    IAgentMemoryDTO,
    IEntityRoleDTO,
    IEventDetailsDTO,
    IEventTrackerDetailsDTO,
    IEventsTrackerListDTO,
    IEventsDAO,
    IEventsListDTO,
    ICreateEventDTO,
    IUpdateEventDTO,
    TGetEventsSortKeys,
    TGetEventTrackerSortKeys,
    IEditPassportDetailsDTO,
    IWebsiteDetailsDTO,
    ICoordinatorDetailsDTO,
    IDeliveryItemDetailsDTO,
    IEventCoordinatorDTO,
    IAllEventConnectionExportDetails,
    DeliveryTeamMemberRole,
    IEventTrackerFilterParams,
    IEventLinks,
    IEventTypes,
    IEventBusinessLines,
    IEventRegions,
    IFilterValue,
    ISelectedFilter,
    IFiltersView,
    EVENT_TRACKER_TAB,
    EVENT_TRACKER_FILTER_VIEW_TAB,
    IOnSyncEvent,
    OnSyncEventType,
    IOnSyncEventSubscriptionResponse,
    IEventRegistrationToken,
    ClientDepartmentValues,
    IEventAttributeValue
};
